import React from "react";
import getConfig from "next/config";
import { add } from "date-fns";
import _get from "lodash/get";

import { productHelper } from "@lib/helpers";
import serializeSchema from "@lib/structuredData";

const {
  publicRuntimeConfig: {
    settings: { cdn, currencyCode, baseUrl, productSchema },
  },
} = getConfig();

// Google rejects a longer name, the same limit as a Merchant Center title
const NAME_MAX_LENGTH = 150;

// Valid GS1 lengths: GTIN-8, UPC-A, EAN-13 and ITF-14
const GTIN_LENGTHS = [8, 12, 13, 14];

// GS1 prefixes reserved for internal or in-store use, as the first digits of
// the 13 digit form. They are valid inside a company but not globally, so
// Google reports them as not globally valid. GTIN-8 has its own ranges.
const RESTRICTED_GTIN13_PREFIXES = ["02", "04", "2"];
const RESTRICTED_GTIN8_PREFIXES = ["0", "2"];

const getProductStockStatus = (product) => {
  const { isSaleable, isPreorder, hasStorePickup, isEol, isVisible } =
    productHelper(product);

  if (isEol) {
    return "Discontinued";
  }

  // We treat out of stock products with resealed items available as out of stock
  if (!isVisible || (!isSaleable && !hasStorePickup)) {
    return "OutOfStock";
  }

  if (!isSaleable && hasStorePickup) {
    return "InStoreOnly";
  }

  if (isPreorder) {
    return "PreOrder";
  }

  return "InStock";
};

const getOfferStockStatus = (offer) => {
  const stockStatus = _get(offer, "stock.status", 0);

  if (stockStatus === 3) {
    return "PreOrder";
  }

  // 1 is in stock, 2 ships on demand; both are orderable
  return stockStatus === 1 || stockStatus === 2 ? "InStock" : "OutOfStock";
};

const getProductRecommendedGenderUse = ({ gender }) => {
  const lowerCaseGender = gender.toLowerCase();

  switch (lowerCaseGender) {
    case "barbati": {
      return "Male";
    }
    case "femei": {
      return "Female";
    }
    default: {
      return "unisex";
    }
  }
};

const getProductImageUrl = (product) => {
  if (product.gallery?.length > 0) {
    return product.gallery.map((image) => `${cdn}${image.file}`);
  }

  if (product.image) {
    return [`${cdn}${product.image}`];
  }
};

const getPrice = (offer) => _get(offer, "price.selling_price", false);

// Dates are taken in UTC so the server and the browser agree on them during
// hydration whatever time zone each one runs in
const toUtcDate = (date) => date.toISOString().slice(0, 10);

// The offer is valid from today, for as many days as the website config allows.
// A validity that expires the next day is already in the past by the time
// Google recrawls a cached page, which makes it drop the price.
const getPriceValidityDates = () => {
  const today = new Date();

  return {
    validFrom: toUtcDate(today),
    priceValidUntil: toUtcDate(
      add(today, { days: productSchema.priceValidDays })
    ),
  };
};

const hasValidGtinCheckDigit = (gtin) => {
  const digits = gtin.split("").map(Number);
  const checkDigit = digits.pop();
  // GS1 weights alternate 3 and 1, starting from the rightmost payload digit
  const weightedSum = digits
    .reverse()
    .reduce((acc, digit, index) => acc + digit * (index % 2 === 0 ? 3 : 1), 0);

  return (10 - (weightedSum % 10)) % 10 === checkDigit;
};

const isRestrictedGtin = (gtin) => {
  if (gtin.length === 8) {
    return RESTRICTED_GTIN8_PREFIXES.some((prefix) => gtin.startsWith(prefix));
  }

  // The ITF-14 packaging indicator is not part of the prefix, and a UPC-A is
  // the 13 digit form without its leading zero
  const gtin13 = gtin.length === 14 ? gtin.slice(1) : gtin.padStart(13, "0");

  return RESTRICTED_GTIN13_PREFIXES.some((prefix) => gtin13.startsWith(prefix));
};

// Many products carry an internal code in ean_codes instead of a real EAN, so
// we only publish a gtin we can verify. An unverifiable code is left out
// rather than replaced with the sku or the manufacturer number.
const getSchemaGtin = (eanCodes) => {
  const gtin = (eanCodes || "").split(",")[0].trim();

  if (!GTIN_LENGTHS.includes(gtin.length) || !/^\d+$/.test(gtin)) {
    return null;
  }

  if (isRestrictedGtin(gtin)) {
    return null;
  }

  return hasValidGtinCheckDigit(gtin) ? gtin : null;
};

const getSchemaName = (name) => {
  if (name.length <= NAME_MAX_LENGTH) {
    return name;
  }

  const nameStart = name.slice(0, NAME_MAX_LENGTH);
  const lastSpaceIndex = nameStart.lastIndexOf(" ");
  const truncatedName =
    lastSpaceIndex > 0 ? nameStart.slice(0, lastSpaceIndex) : nameStart;

  // A cut name should not end on the separator it was cut at
  return truncatedName.replace(/[\s,;:.-]+$/, "");
};

const getSchemaDescription = (product) => {
  const description =
    product.short_description || product.meta_description || product.name;

  // Some descriptions come with inline markup from the content editor
  return description
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const getProductInfoByAttributeCode = ({ product, attribute }) =>
  product.info?.groups &&
  product.info.groups
    .flatMap((group) => group.attributes)
    .find((attr) => attr.attribute_code === attribute);

const getSchemaReviewRating = (reviewsSummary) => {
  const reviewsData = _get(reviewsSummary, "items[0].reviews", null);

  if (reviewsData && reviewsData.value && reviewsData.value !== 0) {
    const reviewsCount = reviewsData.value;
    const reviewsTotalValue = reviewsData.total;

    const reviewRating =
      reviewsTotalValue / reviewsCount <= 5
        ? parseFloat(reviewsTotalValue / reviewsCount)
        : 0;

    const reviewRatingPercentage =
      reviewRating !== 0 ? (reviewRating / 5) * 100 : 0;
    // Google Structured Data Product Schema uses a 5 star scale
    const schemaScaleRatingValue = ((reviewRatingPercentage / 100) * 5).toFixed(
      1
    );

    if (reviewRating) {
      return {
        "@type": "AggregateRating",
        // TODO: remove when review values are migrated
        ratingValue: schemaScaleRatingValue > 1 ? schemaScaleRatingValue : 1,
        reviewCount: reviewsCount,
      };
    }
  }
};

// Emitting a Brand without a name is worse than emitting no brand at all
const getSchemaBrand = (product) =>
  product.brand_name && {
    "@type": "Brand",
    name: product.brand_name,
  };

const getSchemaPeopleAudience = ({ gender }) => {
  const peopleAudience = { "@type": "PeopleAudience" };
  const recommendedGender = getProductRecommendedGenderUse({ gender });

  if (recommendedGender === "unisex") {
    peopleAudience.suggestedGender = recommendedGender;
  } else {
    peopleAudience.suggestedGender = `https://schema.org/${recommendedGender}`;
  }

  return peopleAudience;
};

// No shippingRate on purpose: the delivery fee depends on cart value, weight,
// region and delivery mode, so a single number here would be wrong for most
// orders. Google takes the real rules from the Merchant Center shipping
// settings instead.
const getSchemaShippingDetails = () => {
  const { handlingTime, transitTime } = productSchema;

  return {
    "@type": "OfferShippingDetails",
    shippingDestination: {
      "@type": "DefinedRegion",
      addressCountry: "RO",
    },
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      handlingTime: {
        "@type": "QuantitativeValue",
        minValue: handlingTime.minValue,
        maxValue: handlingTime.maxValue,
        unitCode: "DAY",
      },
      transitTime: {
        "@type": "QuantitativeValue",
        minValue: transitTime.minValue,
        maxValue: transitTime.maxValue,
        unitCode: "DAY",
      },
    },
  };
};

const getSchemaReturnPolicy = () => {
  const { merchantReturnDays, returnFees } = productSchema.returnPolicy;

  return {
    "@type": "MerchantReturnPolicy",
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays,
    applicableCountry: "RO",
    returnMethod: [
      "https://schema.org/ReturnByMail",
      "https://schema.org/ReturnInStore",
    ],
    ...(returnFees && { returnFees }),
  };
};

const getSchemaOffer = ({
  offer,
  availability,
  itemCondition,
  productUrl,
  priceValidityDates,
}) => ({
  "@type": "Offer",
  url: productUrl,
  price: getPrice(offer),
  priceCurrency: currencyCode,
  availability: `https://schema.org/${availability}`,
  itemCondition,
  validFrom: priceValidityDates.validFrom,
  priceValidUntil: priceValidityDates.priceValidUntil,
  shippingDetails: getSchemaShippingDetails(),
  // Google reads the return policy from the offer, not from the product
  hasMerchantReturnPolicy: getSchemaReturnPolicy(),
  ...(offer?.seller?.name && {
    seller: {
      "@type": "Organization",
      name: offer.seller.name,
    },
  }),
});

// A product without a priced offer must not publish an empty Offer: Google
// reads it as a missing price and drops the product from Merchant Center.
// Such a product gets no offers key at all.
const getSchemaOffers = ({
  mainOffer,
  otherOffers,
  productAvailability,
  itemCondition,
  productUrl,
}) => {
  const priceValidityDates = getPriceValidityDates();

  const pricedOffers = [
    ...(getPrice(mainOffer)
      ? [{ offer: mainOffer, availability: productAvailability }]
      : []),
    ...otherOffers
      .filter((offer) => getPrice(offer))
      .map((offer) => ({
        offer,
        availability: getOfferStockStatus(offer),
      })),
  ];

  if (!pricedOffers.length) {
    return null;
  }

  const offers = pricedOffers.map(({ offer, availability }) =>
    getSchemaOffer({
      offer,
      availability,
      itemCondition,
      productUrl,
      priceValidityDates,
    })
  );

  // A single offer goes out as an object, the way Google documents it
  return offers.length === 1 ? offers[0] : offers;
};

const ProductSchema = ({ productData }) => {
  if (_get(productData, "product.name", false)) {
    const { product, reviews_summary } = productData;
    const { mainOffer, otherOffers, isResealed, productNumber } =
      productHelper(product);

    // Same shape as the canonical link, so the url never carries the query
    // string or whatever path the request came in with
    const productUrl = `${baseUrl}/${product.url_key}/cpd/${product.sku}/`;

    const productAvailability = getProductStockStatus(product);
    const itemCondition = isResealed
      ? "https://schema.org/UsedCondition"
      : "https://schema.org/NewCondition";

    const aggregateRating = getSchemaReviewRating(reviews_summary);
    const offers = getSchemaOffers({
      mainOffer,
      otherOffers,
      productAvailability,
      itemCondition,
      productUrl,
    });

    const brand = getSchemaBrand(product);
    const gtin = getSchemaGtin(product.ean_codes);
    const colorOption = getProductInfoByAttributeCode({
      product,
      attribute: "culoare_global",
    });

    const genderOption = getProductInfoByAttributeCode({
      product,
      attribute: "pentru_sex_global",
    });

    const materialOption = getProductInfoByAttributeCode({
      product,
      attribute: "material_global",
    });

    const schema = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: getSchemaName(product.name),
      url: productUrl,
      ...((product.image || product.gallery) && {
        image: getProductImageUrl(product),
      }),
      ...(offers && { offers }),
      description: getSchemaDescription(product),
      sku: product.sku,
      ...(brand && { brand }),
      ...(genderOption?.value && {
        audience: getSchemaPeopleAudience({ gender: genderOption.value }),
      }),
      ...(colorOption?.value && {
        color: colorOption.value,
      }),
      ...(materialOption?.value && { material: materialOption.value }),
      ...(aggregateRating && { aggregateRating }),
      // Kept next to the per offer policy for the consumers reading it here
      hasMerchantReturnPolicy: getSchemaReturnPolicy(),
      ...(gtin && { gtin }),
      ...(productNumber && { mpn: productNumber }),
    };

    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: serializeSchema(schema) }}
      />
    );
  }

  return null;
};

export default ProductSchema;
